import {
  BottomViewFlyers,
  FlyerCard,
  HeaderFixed,
  LoadingSpinner,
  NoFlyersAvailableEmptyState,
  NoFlyersAvailableSectionEmptyState,
  PageTitleHelmet,
} from '@/components';
import { APP_ROUTES, FlyerType, SubscriptionTypeEnum } from '@/config/constants';
import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import AuthenticationContext from '@/context/AuthenticationContext';
import FlyersContext from '@/context/FlyersContext';
import { NavigationContext } from '@/context/NavigationContext';
import { MasonryBreakpointCols } from '@/config/constants';
import {
  BodyLargeEmphasized,
  ButtonFlatMediumEmphasized,
  FlyerboardHeader,
  NavbarSwitcher,
} from '@/design-system';
import { districtVar } from '@/graphql/state';
import {
  AllFlyerFilters,
  FlyerboardTimeFrame,
  FlyerCardThumbnail,
  OwningTypeEnum,
} from '@/graphql/types';
import {
  useChildGrades,
  useFlyerboardPagination,
  useNavigateWithTracking,
  useSchools,
  useURLSearchParams,
} from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import scrollToTop from '@/utils/scroolToTop';
import { useReactiveVar } from '@apollo/client';
import { isEmpty, sortBy } from 'lodash';
import { useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ExploreNewFlyers.styles';
import Masonry from 'react-masonry-css';
import InfiniteScroll from 'react-infinite-scroll-component';
import colors from '@/theme/colors';
import { Divider } from '@mui/material';

export const ExploreNewFlyers: React.FC = () => {
  const navigate = useNavigate();
  const navigateWithTracking = useNavigateWithTracking();
  const { audienceId, audienceType } = useURLSearchParams();
  const district = useReactiveVar(districtVar);
  const districtId = useMemo(() => {
    if (audienceType === 'district') {
      return audienceId;
    }
    return district?.districtId;
  }, [district, audienceId, audienceType]);

  const { isAuthenticated } = useContext(AuthenticationContext);
  const { grades } = useChildGrades();
  const { track, trackEvent } = useAnalytics();
  const { activeTab, onChangeFlyerType, targetTab, isSchoolTabSelected } =
    useContext(FlyersContext);
  const { wasFlyerDetailsPage, isLoading: isUpdatingNav } = useContext(NavigationContext);
  const { hasMultipleChildren, mySchoolId, subscriptions, subscriptionsIds } = useSchools();
  const trackId = 'new_flyers_page';

  useEffect(() => {
    if (!isUpdatingNav) {
      trackEvent({
        event: TrackingEvents.viewFlyerSet,
        event_detail: EventDetails.viewFlyerSet(activeTab, 'new'),
        stage: TrackingStage.discovery,
        ...(wasFlyerDetailsPage
          ? {
              event_from: TrackingEvents.viewFlyerDetails,
              event_detail_from: EventDetails.viewFlyerDetails(activeTab),
              stage_from: TrackingStage.consideration,
            }
          : {}),
      });
    }
  }, [isUpdatingNav, wasFlyerDetailsPage]);

  const trackingFrom = {
    event_from: TrackingEvents.viewFlyerSet,
    event_detail_from: EventDetails.viewFlyerSet(activeTab, 'new'),
    stage_from: TrackingStage.discovery,
  };

  const trackingTabData = useMemo(
    () => ({
      event: TrackingEvents.viewFlyerSet,
      event_detail: EventDetails.viewFlyerSet(targetTab, 'new'),
      stage: TrackingStage.discovery,
      ...trackingFrom,
    }),
    [targetTab, activeTab]
  );

  let baseFilters = useMemo((): AllFlyerFilters | null => {
    if (isAuthenticated && subscriptionsIds.length === 0 || isEmpty(grades)) return null;
    const owningType =
      activeTab == FlyerType.school ? OwningTypeEnum.district : OwningTypeEnum.organization;
    let schoolIds: Number[] = [];
    const isDistrictFlyerboard = audienceType === SubscriptionTypeEnum.district;
    const isCommunityTab = activeTab === FlyerType.community;
    const isMySchools = audienceId === 'all';
    const audienceIdList = isMySchools ? [] : [Number(audienceId)];
    if (isAuthenticated) {
      if (isMySchools || (isDistrictFlyerboard && isCommunityTab)) {
        schoolIds = subscriptionsIds;
      } else {
        if (!isDistrictFlyerboard) {
          schoolIds = audienceIdList;
        }
      }
    } else {
      if (!isDistrictFlyerboard) {
        schoolIds = audienceIdList;
      }
    }
    const allDistrictSchools =
      (isDistrictFlyerboard && !isCommunityTab) ||
      (isCommunityTab && !isAuthenticated && schoolIds.length === 0);

    let filters: AllFlyerFilters = {
      owningType,
      allDistrictSchools,
      schoolIds,
      isNew: true,
      grades: grades.map(g => g.id),
    };

    return filters;
  }, [audienceId, activeTab, isAuthenticated, subscriptionsIds, audienceType, grades]);

  const selectedSubscriptions = useMemo(
    () => sortBy(subscriptions, subscription => subscription.name),
    [subscriptions]
  );

  const onBackButtonClick = () => {
    navigate(`${APP_ROUTES.home}?tab=${activeTab}`);
  };

  const handleClickFlyerType = () => {
    track({
      category: `${trackId}:view_all_flyers:bottom_button`,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label: FlyerType.school === activeTab ? 'Community' : 'School',
    });
    onChangeFlyerType(trackingTabData);
    scrollToTop();
  };

  const invertedFlyerType = activeTab === FlyerType.school ? 'community' : 'school';

  const bottomLabel = `View new ${invertedFlyerType} flyers`;

  const onViewAllFlyerClick = () => {
    let flyerboardParams = '';
    if (isSchoolTabSelected) {
      flyerboardParams = `audienceId=${
        hasMultipleChildren ? mySchoolId : subscriptions[0].id
      }&audienceType=${activeTab}&tab=${activeTab}`;
    } else {
      flyerboardParams = `audienceId=${district?.districtId}&audienceType=${SubscriptionTypeEnum.district}&tab=${activeTab}`;
    }

    navigateWithTracking(`${APP_ROUTES.exploreAllFlyers}?${flyerboardParams}`, {
      stage_from: TrackingStage.discovery,
      event_from: TrackingEvents.viewFlyerSet,
      event_detail_from: EventDetails.viewFlyerSet(activeTab, 'new'),
    });
  };

  const viewAllFlyerLabel = `View All ${activeTab} Flyers`;

  const thisWeekFilters = baseFilters ? {
    ...baseFilters,
    newFlyerboardTimeFrame: FlyerboardTimeFrame.ThisWeek,
  } : null;

  const lastWeekFilters = baseFilters ? {
    ...baseFilters,
    newFlyerboardTimeFrame: FlyerboardTimeFrame.LastWeek,
  } : null;

  const {
    flyers: thisWeekFlyers,
    loading: loadingThisWeekFlyers,
    fetchMoreFlyers: fetchMoreThisWeekFlyers,
    total: thisWeekFlyersTotal,
    isPaginationEnd,
  } = useFlyerboardPagination({
    flyerType: activeTab,
    audienceId,
    districtId: district?.districtId,
    flyerboardFilters: thisWeekFilters || {} as AllFlyerFilters,
    skip: !district || !baseFilters,
  });

  const {
    flyers: lastWeekFlyers,
    loading: loadingLastWeekFlyers,
    fetchMoreFlyers: fetchMoreLastWeekFlyers,
    total: lastWeekFlyersTotal,
    isPaginationEnd: isLastWeekPaginationEnd,
  } = useFlyerboardPagination({
    skip: !district || !baseFilters || !isPaginationEnd,
    flyerType: activeTab,
    audienceId,
    districtId: district?.districtId,
    flyerboardFilters: lastWeekFilters || {} as AllFlyerFilters,
  });

  const loading = loadingThisWeekFlyers || loadingLastWeekFlyers;

  const renderInfiniteScroll = ({
    flyers,
    total,
    nextPage,
  }: {
    flyers: FlyerCardThumbnail[];
    total: number;
    nextPage: () => void;
  }) => {
    const flyersTotal = flyers.length;
    const hasMore = flyersTotal < total;

    return (
      <InfiniteScroll
        style={{ overflow: 'hidden' }}
        dataLength={flyersTotal}
        next={nextPage}
        hasMore={hasMore}
        scrollThreshold={0.6}
        loader={<LoadingSpinner />}
      >
        <Masonry
          breakpointCols={MasonryBreakpointCols}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {flyers.map(
            ({
              flyerId,
              frontImageUrl,
              isFavorite,
              title,
              status,
              type,
              isExpandedCommunity,
              titleTranslations,
            }) => (
              <FlyerCard
                key={flyerId}
                id={flyerId}
                title={title}
                thumbnail={frontImageUrl}
                isFavorite={isFavorite}
                status={status}
                flyerType={type}
                audienceId={audienceId}
                titleTranslations={titleTranslations}
                trackId={trackId}
                trackingEventFrom={trackingFrom.event_detail_from}
                isExpandedCommunity={isExpandedCommunity}
              />
            )
          )}
        </Masonry>
      </InfiniteScroll>
    );
  };

  const thisWeekFlyersList = useMemo(
    () =>
      (thisWeekFlyers.length > 0 || lastWeekFlyers.length > 0) && (
        <div>
          <div className={styles.headerContainer}>
            <BodyLargeEmphasized color={colors.slate}>Last 7 Days</BodyLargeEmphasized>
          </div>
          {thisWeekFlyers.length === 0 ? (
            <NoFlyersAvailableSectionEmptyState />
          ) : (
            renderInfiniteScroll({
              flyers: thisWeekFlyers,
              total: thisWeekFlyersTotal,
              nextPage: fetchMoreThisWeekFlyers,
            })
          )}
        </div>
      ),
    [thisWeekFlyers, lastWeekFlyers, thisWeekFlyersTotal]
  );

  const lastWeekFlyersList = useMemo(
    () =>
      (thisWeekFlyers.length > 0 || lastWeekFlyers.length > 0) && (
        <div className={styles.section}>
          <Divider />
          <div className={styles.headerContainer}>
            <BodyLargeEmphasized color={colors.slate}>Previous 7 Days</BodyLargeEmphasized>
          </div>
          {lastWeekFlyers.length === 0 ? (
            <NoFlyersAvailableSectionEmptyState />
          ) : (
            renderInfiniteScroll({
              flyers: lastWeekFlyers,
              total: lastWeekFlyersTotal,
              nextPage: fetchMoreLastWeekFlyers,
            })
          )}
        </div>
      ),
    [lastWeekFlyers, thisWeekFlyers, lastWeekFlyersTotal]
  );

  if (!baseFilters) return null;

  return (
    <div className={styles.container}>
      <PageTitleHelmet>Explore New Flyers</PageTitleHelmet>
      <HeaderFixed>
        <NavbarSwitcher
          trackId={trackId}
          navBarTitle="New"
          onBackButtonClick={onBackButtonClick}
          newTitle
          trackingData={trackingTabData}
        />
        <FlyerboardHeader
          subscriptions={selectedSubscriptions}
          type={activeTab}
          selectedAudienceId={audienceId}
          trackingEventFrom={trackingFrom.event_detail_from}
        />
      </HeaderFixed>
      <main className={styles.content}>
        <div className={styles.section}>
          {loading && thisWeekFlyers.length === 0 && lastWeekFlyers.length === 0 && (
            <LoadingSpinner />
          )}
          {!loading && thisWeekFlyers.length === 0 && lastWeekFlyers.length === 0 && (
            <NoFlyersAvailableEmptyState />
          )}
          {thisWeekFlyersList}
        </div>
        {lastWeekFlyersList}
        {(thisWeekFlyers.length > 0 || lastWeekFlyers.length > 0) && isLastWeekPaginationEnd && (
          <div className={styles.viewAllFlyersContainer}>
            <ButtonFlatMediumEmphasized onClick={onViewAllFlyerClick} id="view-all-flyers-button">
              {viewAllFlyerLabel}
            </ButtonFlatMediumEmphasized>
          </div>
        )}
      </main>
      <BottomViewFlyers
        label={bottomLabel}
        variant={FlyerType[activeTab]}
        onClick={handleClickFlyerType}
      />
    </div>
  );
};
